import type { SantaReply } from '@/hooks/use-santa-voice-player'
import { voiceFromBase64, voiceFromUrl } from '@/hooks/use-santa-voice-player'
import type { ChatEntry, ChatOpening, ChatTurn } from '@/lib/santa-chat/chat-state'
import type {
  PublicChatMessageResult,
  PublicChatOpenResult,
  PublicChatReply,
} from '@/server/application/santa-chat/public/public-chat-contract'
import type { SantaGesture } from '@/server/domain/santa-chat'

/**
 * Las respuestas del chat público en la forma del chat compartido: notas
 * (`ChatEntry`), aperturas y turnos, más lo que cada resultado significa para
 * la sesión. Funciones puras: el hook solo las despacha.
 */

/**
 * Cómo acaba la sesión. `full`: alta como CTA principal + llamada y vídeo
 * (DD10). `signupOnly`: la sesión acabó en `seekAdult` (guarda 9): sin venta ni
 * llamada.
 */
export type ClosingOffer = 'full' | 'signupOnly'

/** El evento del funnel que cierra la sesión, si lo hay. */
export type ClosingEvent = 'completed' | 'closedForToday' | null

function entry(id: string, author: ChatEntry['author'], text: string, gesture: SantaGesture | null): ChatEntry {
  return {
    id,
    author,
    text,
    inputMode: author === 'child' ? 'text' : null,
    gesture,
    flagged: false,
    createdAt: new Date().toISOString(),
    delivery: 'delivered',
  }
}

export function santaEntry(id: string, text: string, gesture: SantaGesture): ChatEntry {
  return entry(id, 'santa', text, gesture)
}

/** La voz de una respuesta: el mp3 enlatado (URL) o la voz en vivo (base64), y la cola enlatada del saludo con nombre. */
export function spoken(
  id: string,
  gesture: SantaGesture,
  said: { audioUrl?: string; audioBase64?: string; tailAudioUrl?: string }
): SantaReply {
  const voice = said.audioUrl ? voiceFromUrl(said.audioUrl) : voiceFromBase64(said.audioBase64 ?? '', said.tailAudioUrl ?? null)
  return { id, gesture, voice }
}

export const GREETING_ID = 'santa-greeting'
const CLOSED_ID = 'santa-closed'

/** La apertura: el saludo; o, si el dispositivo, la IP o el taller ya no dan para hoy, lo que dice Papá Noel y el tope. */
export function openingOf(result: PublicChatOpenResult): { opening: ChatOpening; reply: SantaReply } {
  if (result.kind === 'opened') {
    const { greeting } = result
    return {
      opening: { entries: [santaEntry(GREETING_ID, greeting.text, greeting.gesture)], limitReached: false, limitResetsAt: null, tier: null },
      reply: spoken(GREETING_ID, greeting.gesture, greeting),
    }
  }
  return {
    opening: { entries: [santaEntry(CLOSED_ID, result.text, 'talk')], limitReached: true, limitResetsAt: null, tier: null },
    reply: spoken(CLOSED_ID, 'talk', result),
  }
}

/**
 * Una respuesta del modelo como turno del chat. Sin mensajes por gastar, el
 * chat queda en su tope y ofrece «llamar» (que aquí lleva al alta), salvo tras
 * buscar a un adulto: ahí no se ofrece nada.
 */
function turnOf(localId: string, childText: string, reply: PublicChatReply): ChatTurn {
  const ended = reply.messagesLeft === 0
  const safety = reply.safety === 'seekAdult'
  return {
    child: entry(localId, 'child', childText, null),
    santa: santaEntry(`${localId}-santa`, reply.text, reply.gesture),
    limitReached: ended,
    limitResetsAt: null,
    showCallCta: ended && !safety,
    safety,
    tier: null,
  }
}

/** Lo que el chat hace con un resultado de `messages`: qué despachar, qué decir y cómo sigue la sesión. */
export interface TurnOutcome {
  /** Las acciones para el chat, en orden. */
  actions: ({ type: 'turnArrived'; localId: string; turn: ChatTurn }
    | { type: 'turnFallback'; localId: string; notice: { id: string; reason: 'elvesError'; text: string; gesture: SantaGesture } }
    | { type: 'limitHit'; localId: string; resetsAt: null }
    | { type: 'santaSaid'; entry: ChatEntry })[]
  /** Lo que suena, en orden: la despedida espera a que acabe la respuesta. */
  voices: SantaReply[]
  /** El token con el que sigue la sesión; `null` si se acabó. */
  nextToken: string | null
  messagesLeft: number
  closing: ClosingOffer | null
  closingEvent: ClosingEvent
  /** El nombre que el niño acaba de decir, ya saneado (pasa al alta de perfil, AC2.3). */
  childName?: string
}

/** El cierre sin respuesta del modelo (`session_limit`, `closed_for_today`): Papá Noel se despide y el chat queda en su tope. */
function closedTurn(localId: string, text: string, said: { audioUrl: string }, closingEvent: ClosingEvent): TurnOutcome {
  const id = `${localId}-santa`
  return {
    actions: [
      { type: 'limitHit', localId, resetsAt: null },
      { type: 'santaSaid', entry: santaEntry(id, text, 'goodbye') },
    ],
    voices: [spoken(id, 'goodbye', said)],
    nextToken: null,
    messagesLeft: 0,
    closing: 'full',
    closingEvent,
  }
}

function replyOutcome(localId: string, childText: string, reply: PublicChatReply): TurnOutcome {
  const turn = turnOf(localId, childText, reply)
  const said = spoken(turn.santa.id, reply.gesture, reply)
  const base = { messagesLeft: reply.messagesLeft, childName: reply.childName }
  if (reply.messagesLeft > 0) {
    return { ...base, actions: [{ type: 'turnArrived', localId, turn }], voices: [said], nextToken: reply.token, closing: null, closingEvent: null }
  }
  // Último mensaje. Tras un seekAdult (guarda 9) no hay despedida ni venta.
  const farewell = reply.safety === 'seekAdult' ? undefined : reply.farewell
  const farewellId = `${turn.santa.id}-farewell`
  return {
    ...base,
    actions: [
      { type: 'turnArrived', localId, turn },
      ...(farewell ? [{ type: 'santaSaid' as const, entry: santaEntry(farewellId, farewell.text, 'goodbye') }] : []),
    ],
    voices: farewell ? [said, spoken(farewellId, 'goodbye', farewell)] : [said],
    nextToken: null,
    closing: reply.safety === 'seekAdult' ? 'signupOnly' : 'full',
    closingEvent: 'completed',
  }
}

/** Cada resultado de `messages`, decidido en un único `switch` exhaustivo. */
export function turnOutcomeOf(result: PublicChatMessageResult, localId: string, childText: string): TurnOutcome {
  switch (result.kind) {
    case 'reply':
      return replyOutcome(localId, childText, result)
    case 'fallback': {
      const id = `${localId}-santa`
      const ended = result.messagesLeft === 0
      return {
        actions: [{ type: 'turnFallback', localId, notice: { id, reason: result.reason, text: result.text, gesture: 'talk' } }],
        voices: [spoken(id, 'talk', result)],
        nextToken: ended ? null : result.token,
        messagesLeft: result.messagesLeft,
        closing: ended ? 'full' : null,
        closingEvent: ended ? 'completed' : null,
      }
    }
    case 'session_limit':
      // El tope ya estaba gastado: no completa nada nuevo.
      return closedTurn(localId, result.text, result, null)
    case 'closed_for_today':
      return closedTurn(localId, result.text, result, 'closedForToday')
    default: {
      const unhandled: never = result
      return unhandled
    }
  }
}
